import { motion } from "framer-motion";

const chapters = [
  {
    n: "01",
    title: "Preservation is no longer a strategy.",
    body: "For a generation, private and family capital defined itself by what it protected. But inflation, geopolitical fragility, and generational turnover have rewritten the mandate. Preservation, alone, is now a form of quiet erosion.",
  },
  {
    n: "02",
    title: "Active value creation is the new mandate.",
    body: "Principals are being asked to compound capital across venture, private markets, operating businesses, and impact — with the same discipline that defined public-market wealth management. The mandate has expanded. Most in-house teams have not.",
  },
  {
    n: "03",
    title: "In-house teams cannot scale fast enough.",
    body: "Hiring cycles are 9—18 months. Building a full venture, diligence, or AI function internally can cost $5—15M annually before the first thesis is tested. Talent is scarce, geographies are fragmented, and the ramp is slow.",
  },
  {
    n: "04",
    title: "A specialized capability center — as a service.",
    body: "Advik Ventures architects, staffs, and operates modular capability centers on your behalf. In 90—120 days, you have a working desk — governance, talent, tooling, and reporting — indistinguishable from an internal team, at 40—60% lower cost.",
  },
];

export default function Manifesto() {
  return (
    <section
      id="approach"
      className="relative py-24 md:py-40 border-t border-[var(--line)]"
      data-testid="manifesto-section"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
              I. Manifesto
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              The mandate has changed. So must the machinery behind it.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 md:pt-6">
            <p className="text-ivory/70 text-base md:text-lg leading-relaxed">
              A working thesis in four movements — read slowly. Written for
              principals, next-gen stewards, and the operators who translate
              conviction into deployed capital.
            </p>
            <p className="mt-5 text-ivory/60 text-sm md:text-base leading-relaxed border-l border-gold/50 pl-4">
              Where the mandate demands it, we stand up{" "}
              <span className="text-gold">institutional-grade digital asset infrastructure</span>{" "}
              — custody architecture, tokenization rails, on-chain treasury, and
              compliance posture — engineered to the same standard as the rest
              of your capability center.
            </p>
          </div>
        </div>

        <div className="space-y-24 md:space-y-32">
          {chapters.map((c, i) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
              className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12"
            >
              <div className="md:col-span-3 flex md:block items-baseline gap-4">
                <div className="chapter-num text-6xl md:text-8xl text-gold leading-none">
                  {c.n}
                </div>
                <div className="md:mt-4 text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                  Chapter {c.n}
                </div>
              </div>
              <div className="md:col-span-8 md:col-start-5">
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-ivory">
                  {c.title}
                </h3>
                <div className="hairline my-6 md:my-8 max-w-md" />
                <p className="text-ivory/70 text-base md:text-lg leading-relaxed max-w-2xl">
                  {c.body}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
